import React, { useState, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';
import { Button } from '../ui/button';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: 'Herzlich willkommen bei FinanzierungsPilot! Wie kann ich Ihnen heute helfen?',
      time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const [inputValue, setInputValue] = useState('');

  const quickReplies = [
    'Ich möchte eine Finanzierung anfragen',
    'Wie funktioniert der Vergleich?',
    'Beratungstermin vereinbaren',
    'Kontaktdaten'
  ];

  const botResponses = {
    'finanzierung': 'Gerne helfe ich Ihnen bei Ihrer Finanzierungsanfrage! Nutzen Sie unser Online-Formular unter "Anfrage stellen" oder rufen Sie uns an unter +49 (0) 15511 427039.',
    'vergleich': 'Unser Vergleich ist einfach: 1) Anfrage stellen 2) Wir vergleichen über 500 Banken 3) Sie erhalten die besten Angebote. Der Service ist kostenlos!',
    'termin': 'Einen Beratungstermin können Sie direkt auf unserer Seite "Beratung" buchen oder rufen Sie uns an. Wir sind Mo-Fr von 9-18 Uhr erreichbar.',
    'kontakt': 'Sie erreichen uns unter:\nTelefon: +49 (0) 15511 427039\nE-Mail: info@finanzierungspilot.net\nMo-Fr: 9:00 - 18:00 Uhr',
    'default': 'Vielen Dank für Ihre Nachricht! Ein Berater wird sich in Kürze bei Ihnen melden. Alternativ erreichen Sie uns unter +49 (0) 15511 427039.'
  };

  const handleSendMessage = (text) => {
    if (!text.trim()) return;

    const newUserMessage = {
      id: messages.length + 1,
      type: 'user',
      text: text,
      time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, newUserMessage]);
    setInputValue('');

    // Simulate bot response
    setTimeout(() => {
      let response = botResponses.default;
      const lowerText = text.toLowerCase();
      
      if (lowerText.includes('finanzierung') || lowerText.includes('anfrag')) {
        response = botResponses.finanzierung;
      } else if (lowerText.includes('vergleich') || lowerText.includes('funktioniert')) {
        response = botResponses.vergleich;
      } else if (lowerText.includes('termin') || lowerText.includes('beratung')) {
        response = botResponses.termin;
      } else if (lowerText.includes('kontakt') || lowerText.includes('telefon') || lowerText.includes('email')) {
        response = botResponses.kontakt;
      }

      const newBotMessage = {
        id: messages.length + 2,
        type: 'bot',
        text: response,
        time: new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
      };

      setMessages(prev => [...prev, newBotMessage]);
    }, 1000);
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
          </>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-slideUp">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Bot className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">FinanzierungsPilot Chat</p>
                <p className="text-xs text-blue-200">Wir antworten sofort</p>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] ${
                  message.type === 'user'
                    ? 'bg-blue-600 text-white rounded-2xl rounded-br-md'
                    : 'bg-white text-slate-800 rounded-2xl rounded-bl-md shadow-sm border border-slate-100'
                } px-4 py-3`}>
                  <p className="text-sm whitespace-pre-line">{message.text}</p>
                  <p className={`text-xs mt-1 ${
                    message.type === 'user' ? 'text-blue-200' : 'text-slate-400'
                  }`}>
                    {message.time}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Replies */}
          {messages.length <= 2 && (
            <div className="px-4 py-3 border-t border-slate-100 bg-white">
              <p className="text-xs text-slate-500 mb-2">Schnellantworten:</p>
              <div className="flex flex-wrap gap-2">
                {quickReplies.map((reply, index) => (
                  <button
                    key={index}
                    onClick={() => handleSendMessage(reply)}
                    className="text-xs bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-700 px-3 py-1.5 rounded-full transition-colors"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-slate-200 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(inputValue)}
                placeholder="Ihre Nachricht..."
                className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <Button
                onClick={() => handleSendMessage(inputValue)}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-4"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
